import { type CSSProperties, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DefinitionTooltip,
  dashboardDataTable as dt,
} from "src/components/common";
import type {
  SalesAttributionChannelRow,
  SalesAttributionMetric,
} from "../utils/salesAttributionTypes";

const HEATMAP_RGB = "43 116 228";

const heatmapCellStyle = (shareOfMax: number): CSSProperties => {
  const intensity = Math.min(1, Math.max(0, shareOfMax));
  return {
    backgroundColor: `rgb(${HEATMAP_RGB} / ${(0.12 + intensity * 0.88).toFixed(3)})`,
    color: intensity > 0.55 ? "#ffffff" : undefined,
    fontWeight: intensity > 0.55 ? 600 : undefined,
  };
};

type NumericColumnKey =
  | "spend"
  | "platformRevenue"
  | "attributedRevenue"
  | "roas"
  | "orders"
  | "impressions"
  | "clicks"
  | "ctr";

interface ScorecardColumn {
  key: NumericColumnKey;
  labelDefault: string;
  helpDefault: string;
}

const COLUMNS: ScorecardColumn[] = [
  {
    key: "spend",
    labelDefault: "Spend",
    helpDefault: "Spend (coming from the platform).",
  },
  {
    key: "platformRevenue",
    labelDefault: "Platform Rev.",
    helpDefault:
      "Revenue the ad platform reports itself. Computed independently of attributed revenue, so the two will not match.",
  },
  {
    key: "attributedRevenue",
    labelDefault: "Attributed Rev.",
    helpDefault:
      "Zid order revenue whose referrer resolves to this channel. Computed independently of platform revenue, so the two will not match.",
  },
  {
    key: "roas",
    labelDefault: "ROAS",
    helpDefault: "Platform-reported ROAS: platform revenue ÷ spend.",
  },
  {
    key: "orders",
    labelDefault: "Orders",
    helpDefault: "Orders (coming from the platform).",
  },
  {
    key: "impressions",
    labelDefault: "Impr.",
    helpDefault: "Impressions (coming from the platform).",
  },
  {
    key: "clicks",
    labelDefault: "Clicks",
    helpDefault: "Clicks (coming from the platform).",
  },
  {
    key: "ctr",
    labelDefault: "CTR",
    helpDefault: "CTR (coming from the platform).",
  },
];

const metricOf = (
  row: SalesAttributionChannelRow,
  key: NumericColumnKey,
): SalesAttributionMetric => row[key];

interface ChannelScorecardTableProps {
  rows: SalesAttributionChannelRow[];
  /** Server-summed total row; `null` when neither ad platform is connected. */
  total: SalesAttributionChannelRow | null;
}

export const ChannelScorecardTable: FC<ChannelScorecardTableProps> = ({ rows, total }) => {
  const { t } = useTranslation();

  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) => b.attributedRevenue.value - a.attributedRevenue.value,
      ),
    [rows],
  );

  const maxima = useMemo(
    () =>
      COLUMNS.reduce<Record<NumericColumnKey, number>>(
        (result, column) => ({
          ...result,
          [column.key]: rows.reduce(
            (max, row) => Math.max(max, metricOf(row, column.key).value),
            0,
          ),
        }),
        {} as Record<NumericColumnKey, number>,
      ),
    [rows],
  );

  const columnLabel = (column: ScorecardColumn) =>
    t(`salesAttribution.ui.scorecardColumnLabels.${column.key}`, {
      defaultValue: column.labelDefault,
    });

  if (rows.length === 0) {
    return (
      <div className={dt.emptyState}>
        <p className={dt.emptyStateTitle}>
          {t("salesAttribution.ui.scorecardEmptyTitle", { defaultValue: "No channels to score" })}
        </p>
        <p className={dt.emptyStateDescription}>
          {t("salesAttribution.ui.scorecardEmptyDescription", {
            defaultValue: "No channel activity was recorded for the selected date range.",
          })}
        </p>
      </div>
    );
  }

  return (
    <DashboardDataTableScroll>
      <DashboardDataTableTable className="sales-attribution__scorecard">
        <caption className="sales-attribution__visually-hidden">
          {t("salesAttribution.ui.scorecardCaption", {
            defaultValue:
              "Channel scorecard. Cell shading indicates each value's share of the highest value in its column.",
          })}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {t("salesAttribution.ui.scorecardChannelHeader", { defaultValue: "Channel" })}
            </th>
            {COLUMNS.map(column => (
              <th key={column.key} scope="col" className={dt.thCenter}>
                <span className={dt.thHelpRow}>
                  {columnLabel(column)}
                  <DefinitionTooltip
                    label={columnLabel(column)}
                    info={{
                      description: t(
                        `salesAttribution.tooltips.scorecardColumns.${column.key}`,
                        { defaultValue: column.helpDefault },
                      ),
                    }}
                    align="end"
                  />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(row => (
            <tr key={row.channelId}>
              <td>{row.channelName}</td>
              {COLUMNS.map(column => {
                const cell = metricOf(row, column.key);
                const columnMax = maxima[column.key];
                const shareOfMax = columnMax > 0 ? cell.value / columnMax : 0;
                return (
                  <td
                    key={column.key}
                    className={`${dt.tdCenter} ${dt.number} sales-attribution__scorecard-cell`}
                    style={heatmapCellStyle(shareOfMax)}
                    title={t("salesAttribution.tooltips.scorecardCell", {
                      defaultValue:
                        "{{channelName}} — {{columnLabel}}: {{value}}. {{help}}",
                      channelName: row.channelName,
                      columnLabel: columnLabel(column),
                      value: cell.formattedValue,
                      help: t(
                        `salesAttribution.tooltips.scorecardColumns.${column.key}`,
                        { defaultValue: column.helpDefault },
                      ),
                    })}
                  >
                    {cell.formattedValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {total ? (
          <tfoot>
            <tr className="sales-attribution__scorecard-divider" aria-hidden>
              <td colSpan={COLUMNS.length + 1} />
            </tr>
            <tr className="sales-attribution__scorecard-total">
              <td>{t("salesAttribution.ui.scorecardTotal", { defaultValue: "Total" })}</td>
              {COLUMNS.map(column => (
                <td
                  key={column.key}
                  className={`${dt.tdCenter} ${dt.number} sales-attribution__scorecard-total-cell`}
                >
                  {metricOf(total, column.key).formattedValue}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </DashboardDataTableTable>

      <div className="sales-attribution__scorecard-scale" aria-hidden>
        <span>{t("salesAttribution.ui.scorecardLow", { defaultValue: "Low" })}</span>
        <span className="sales-attribution__scorecard-scale-bar" />
        <span>{t("salesAttribution.ui.scorecardHigh", { defaultValue: "High" })}</span>
      </div>
    </DashboardDataTableScroll>
  );
};

export default ChannelScorecardTable;
